// Music — the adaptive soundtrack.
// ONE looping 32-bar A-DORIAN piece (form A A' B A'':
// period + bridge + reprise) rendered procedurally from the struck-bone
// voice family (no audio assets), split into seven stems (pad / bass / sub /
// arp / melody / perc / war) that fade per SITUATION:
// idle/gather/craft/fish/mine_chop/combat/travel. A VIBE is a performance of
// the fixed composition (tempo/swing/lead/bass groove/kit/pad); combat swaps
// kits regardless of vibe: the vibe's perc drops OUT and the war kit + 808
// sub slam IN — both COMBAT-ONLY. The situation detector reads the vsj
// (current_activity state + work family via sfx.familyFor, live fight
// slice); the whole bus rests gently low-passed during normal play and OPENS
// fully in the map view; travel-running lifts pitch a touch. Playback is
// gated on the vsj screen (silent on boot/login) AND the browser's autoplay
// unlock (first gesture). Stems render in chunked async slices
// (never blocks a frame for long);
// a generation counter discards renders finished for a stale vibe; a session
// cache makes vibe revisits instant. Graph: stem source -> stem gain ->
// music bus (volume/mute) -> low-pass -> destination.

import { getCtx, onUnlock, armUnlock, loadAudioSettings, saveAudioSettings, mulberry32 } from './ctx';
import { familyFor, BAR, DARK } from './sfx';
import * as vsj from '../vsj';

const RATE_LOW = 11025; // pad/bass/sub — nothing above ~5.5 kHz lives there
const RATE_HIGH = 22050; // arp/melody strikes / perc kit
const BARS = 32;
const PITCH_HEADROOM = 1.1; // partial culling headroom for the run lift
const TAU = Math.PI * 2;

// Music tunables (palette.gd MUSIC_* mirrored — not theme colors).
const DEFAULT_VOLUME = 0.5;
const RMS_DB = -22.0; // per-stem loudness target (sits under sfx)
const PEAK = 0.7; // hard per-stem peak ceiling
const FADE_S = 1.8; // stem crossfade on situation change
const RUN_PITCH = 1.03; // travel-run tempo/pitch lift (subtle)
const PITCH_FADE_S = 0.8;
const LPF_OPEN_HZ = 18000.0; // fully open (map view: the soundtrack leads)
const LPF_WORLD_HZ = 2200.0; // normal-play dampen (rests under the game)
const LPF_FADE_S = 0.6;

const STEMS = ['pad', 'bass', 'sub', 'arp', 'melody', 'perc', 'war'] as const;
type Stem = (typeof STEMS)[number];
const STEM_RATE: Record<Stem, number> = {
  pad: RATE_LOW, bass: RATE_LOW, sub: RATE_LOW,
  arp: RATE_HIGH, melody: RATE_HIGH, perc: RATE_HIGH, war: RATE_HIGH,
};

/** Situation -> stem gains (0..1). ONE table = the whole arrangement. */
const SITUATION_MIX: Record<string, Record<string, number>> = {
  idle:      { pad: 0.85, bass: 0.2,  sub: 0.0, arp: 0.4,  melody: 0.3,  perc: 0.0,  war: 0.0 },
  gather:    { pad: 0.8,  bass: 0.5,  sub: 0.0, arp: 0.55, melody: 0.55, perc: 0.25, war: 0.0 },
  craft:     { pad: 0.6,  bass: 0.7,  sub: 0.0, arp: 0.7,  melody: 0.6,  perc: 0.55, war: 0.0 },
  fish:      { pad: 0.9,  bass: 0.35, sub: 0.0, arp: 0.45, melody: 0.45, perc: 0.0,  war: 0.0 },
  mine_chop: { pad: 0.4,  bass: 0.9,  sub: 0.0, arp: 0.5,  melody: 0.45, perc: 1.0,  war: 0.0 },
  combat:    { pad: 0.15, bass: 0.7,  sub: 1.0, arp: 0.6,  melody: 0.85, perc: 0.0,  war: 1.0 },
  travel:    { pad: 0.6,  bass: 0.8,  sub: 0.0, arp: 0.8,  melody: 1.0,  perc: 0.6,  war: 0.0 },
};

// ---------------------------------------------------------- the composition
// All pitches are semitones from A4 (440 Hz). See music.gd for the theory.

const CHORDS: { pad: number[]; bass: number; arp: number[] }[] = [
  { pad: [-12, -9, -5], bass: -24, arp: [-5, 0, 3, 7] },   // 0 Am: A3 C4 E4
  { pad: [-14, -9, -5], bass: -21, arp: [-5, -2, 3, 7] },  // 1 C:  G3 C4 E4
  { pad: [-14, -10, -7], bass: -26, arp: [-7, -2, 2, 5] }, // 2 G:  G3 B3 D4
  { pad: [-12, -7, -3], bass: -19, arp: [-7, -3, 0, 5] },  // 3 D:  A3 D4 F#4 (Dorian IV)
  { pad: [-14, -10, -5], bass: -29, arp: [-5, -2, 2, 7] }, // 4 Em: G3 B3 E4
];

/** One chord per bar; form A A' B A''. */
const PROG = [0, 1, 2, 3, 0, 1, 2, 4, 0, 1, 2, 3, 1, 3, 0, 2,
  1, 2, 0, 4, 1, 2, 3, 2, 0, 1, 2, 3, 1, 3, 0, 2];

/** Rhythm section rests here (0-indexed bar 24, closing the bridge). */
const BREAK_BARS = [23];

// Melody bars: [beat-in-bar, semitones-from-A4, duration-in-beats].
// M1 is THE motif; N1 the bridge motif — reused bar arrays ARE the form.
const M1 = [[0.0, 0, 1.0], [1.0, 3, 0.5], [1.5, 5, 0.5], [2.0, 7, 1.5], [3.5, 3, 0.5]];
const M2 = [[0.0, 3, 1.5], [1.5, 5, 0.5], [2.0, 3, 1.0], [3.0, 0, 1.0]];
const M3 = [[0.0, -2, 1.0], [1.0, 2, 0.5], [1.5, 3, 0.5], [2.0, 5, 1.5], [3.5, 3, 0.5]];
const M4 = [[0.0, 5, 1.0], [1.0, 7, 0.5], [1.5, 5, 0.5], [2.0, 0, 1.0], [3.0, -3, 1.0]]; // ends on F#: Dorian color
const M7 = [[0.0, 5, 1.0], [1.0, 3, 0.5], [1.5, 2, 0.5], [2.0, -2, 1.5], [3.5, 0, 0.5]];
const M8 = [[0.0, 2, 2.0], [2.0, -2, 1.0], [3.0, 2, 1.0]]; // hangs on B: question
const M13 = [[0.0, 10, 1.0], [1.0, 7, 0.5], [1.5, 5, 0.5], [2.0, 3, 1.5], [3.5, 5, 0.5]]; // G5 local peak
const M14 = [[0.0, 9, 1.0], [1.0, 7, 0.5], [1.5, 5, 0.5], [2.0, 0, 1.0], [3.0, 2, 1.0]]; // F#5 answers over D
const M15 = [[0.0, 3, 1.5], [1.5, 2, 0.5], [2.0, 0, 2.0]]; // C-B-A: home
const M16 = [[0.0, -2, 1.0], [2.0, 5, 1.0], [3.0, 2, 1.0]]; // G-D-B: bVII turnaround
const N1 = [[0.0, 10, 1.0], [1.0, 7, 0.5], [1.5, 10, 0.5], [2.0, 7, 1.5], [3.5, 5, 0.5]]; // bridge motif, high
const N2 = [[0.0, 5, 1.0], [1.0, 7, 0.5], [1.5, 5, 0.5], [2.0, 2, 1.0], [3.0, 5, 1.0]];
const N3 = [[0.0, 12, 1.0], [1.0, 10, 0.5], [1.5, 7, 0.5], [2.0, 3, 1.5], [3.5, 2, 0.5]]; // A5: THE climax
const N4 = [[0.0, 7, 1.0], [1.0, 5, 0.5], [1.5, 3, 0.5], [2.0, 2, 1.5], [3.5, 0, 0.5]];
const N5 = [[0.0, 9, 1.0], [1.0, 7, 0.5], [1.5, 5, 0.5], [2.0, 5, 1.0], [3.0, 2, 1.0]];
const NBRK = [[3.0, 2, 1.0]]; // break bar: one pickup home
const MELODY_BARS = [M1, M2, M3, M4, M1, M2, M7, M8,
  M1, M2, M3, M4, M13, M14, M15, M16,
  N1, N2, N3, N4, N1, N2, N5, NBRK,
  M1, M2, M3, M4, M13, M14, M15, M16];

/** Broken-chord eighths (indices into a chord's 4 arp tones): up-and-back. */
const ARP_PATTERN = [0, 1, 2, 3, 2, 1, 0, 2];

// ------------------------------------------------------------------ vibes
// A vibe = the PERFORMANCE of the fixed composition (see music.gd).
interface Vibe {
  label: string; tempo: number; swing: number; kit: string;
  lead: { partials: number[][]; attack: number; curve: number;
    vib_hz: number; vib_semis: number; amp: number; dur: number };
  arp: { mode: string; amp: number; decay: number };
  bass: { groove: [number, boolean, number, number][] };
  pad: { amp: number; attack: number; pump: number };
}
export const VIBES: Record<string, Vibe> = {
  folk: { label: 'Folk', tempo: 118.125, swing: 0.62, kit: 'folk',
    lead: { partials: [[1.0, 1.0], [2.0, 0.28], [3.0, 0.1], [4.0, 0.04]],
      attack: 0.05, curve: 0.9, vib_hz: 5.2, vib_semis: 0.18, amp: 1.0, dur: 0.95 },
    arp: { mode: 'picked', amp: 1.0, decay: 0.8 },
    bass: { groove: [[0.0, false, 0.5, 0.95], [1.0, true, 0.4, 0.6],
      [2.0, false, 0.5, 0.8], [3.0, true, 0.4, 0.6]] },
    pad: { amp: 0.3, attack: 0.3, pump: 0.0 } },
  rnb: { label: 'RnB', tempo: 75.0, swing: 0.67, kit: 'rnb',
    lead: { partials: [[1.0, 1.0], [2.0, 0.12], [4.0, 0.2], [7.0, 0.05]],
      attack: 0.006, curve: 1.6, vib_hz: 0.0, vib_semis: 0.0, amp: 0.95, dur: 1.0 },
    arp: { mode: 'picked', amp: 0.8, decay: 0.5 },
    bass: { groove: [[0.0, false, 1.0, 0.95], [1.75, false, 0.3, 0.5],
      [2.5, true, 0.5, 0.7], [3.75, false, 0.3, 0.55]] },
    pad: { amp: 0.35, attack: 0.4, pump: 0.0 } },
  edm: { label: 'EDM', tempo: 126.0, swing: 0.5, kit: 'edm',
    lead: { partials: [[1.0, 1.0], [2.0, 0.5], [3.0, 0.33], [4.0, 0.25],
      [5.0, 0.18], [6.0, 0.12]],
      attack: 0.012, curve: 0.8, vib_hz: 4.5, vib_semis: 0.1, amp: 0.85, dur: 0.9 },
    arp: { mode: 'picked', amp: 1.2, decay: 1.6 },
    bass: { groove: [[0.5, false, 0.45, 0.85], [1.5, false, 0.45, 0.85],
      [2.5, false, 0.45, 0.85], [3.5, false, 0.45, 0.85]] },
    pad: { amp: 0.4, attack: 0.15, pump: 0.6 } },
  reggae: { label: 'Reggae', tempo: 84.0, swing: 0.58, kit: 'reggae',
    lead: { partials: [[1.0, 1.0], [2.0, 0.5], [3.0, 0.22], [4.0, 0.1]],
      attack: 0.03, curve: 1.1, vib_hz: 5.5, vib_semis: 0.22, amp: 0.95, dur: 0.9 },
    arp: { mode: 'skank', amp: 1.0, decay: 2.2 },
    bass: { groove: [[0.0, false, 0.9, 0.95], [2.5, true, 0.45, 0.55],
      [3.0, false, 0.45, 0.7]] },
    pad: { amp: 0.2, attack: 0.3, pump: 0.0 } },
  country: { label: 'Country', tempo: 112.0, swing: 0.55, kit: 'country',
    lead: { partials: [[1.0, 1.0], [2.0, 0.42], [3.0, 0.18], [5.0, 0.06]],
      attack: 0.004, curve: 2.0, vib_hz: 6.0, vib_semis: 0.1, amp: 1.0, dur: 0.7 },
    arp: { mode: 'picked', amp: 1.1, decay: 1.4 },
    bass: { groove: [[0.0, false, 0.9, 0.95], [2.0, true, 0.9, 0.85]] },
    pad: { amp: 0.22, attack: 0.25, pump: 0.0 } },
};

/** Screens where the soundtrack plays (silent on boot/login). */
const MUSIC_SCREENS = ['char_select', 'world'];

export let muted = false;
export let volume = DEFAULT_VOLUME;
export let situation = 'idle';
let mapOpen = false;
let running = false;
let screenOn = false;

// Vibe-derived timing.
export let vibeName = 'folk';
let tempo = 118.125;
let beatS = 60.0 / tempo;
let barS = beatS * 4.0;
let loopS = barS * BARS;
let swingAmt = 0.62;
let vibe: Vibe = VIBES['folk'];

type StemData = Record<Stem, Float32Array>;
let musicBus: GainNode | null = null;
let lpf: BiquadFilterNode | null = null;
let players: Partial<Record<Stem, { src: AudioBufferSourceNode; gain: GainNode }>> = {};
let streams: StemData | null = null; // current vibe's rendered stems
let started = false;
let rendering = false;
let renderGen = 0; // bumped per requested render; stale results discarded
const streamCache: Record<string, StemData> = {}; // per-vibe session cache

export function init(): void {
  const s = loadAudioSettings();
  muted = !!s.muted;
  volume = Math.max(0, Math.min(1, Number(s.music_volume ?? DEFAULT_VOLUME)));
  const vn = String(s.music_vibe ?? 'folk');
  if (VIBES[vn]) applyVibe(vn);
  vsj.onChange((changed) => {
    if (changed.has('current_activity') || changed.has('fight') || changed.has('*'))
      refreshFromVsj();
    if (changed.has('screen') || changed.has('*')) refreshScreenGate();
  });
  armUnlock();
  onUnlock(() => {
    setupBus();
    refreshScreenGate();
    if (streams && screenOn && !started) startPlayback();
  });
  void startRender(); // stems render at boot so entry is instant
}

function setupBus(): void {
  const ctx = getCtx();
  if (!ctx || musicBus) return;
  musicBus = ctx.createGain();
  // the world-play dampen: a low-pass that rests gently closed and opens
  // fully only in the map view
  lpf = ctx.createBiquadFilter();
  lpf.type = 'lowpass';
  lpf.frequency.value = mapOpen ? LPF_OPEN_HZ : LPF_WORLD_HZ;
  musicBus.connect(lpf);
  lpf.connect(ctx.destination);
  applyVolume();
}

// ------------------------------------------------------------- situations

/** Pure mapping: fight beats travel beats work. */
export function situationFor(fightLive: boolean, state: string, family: string): string {
  if (fightLive) return 'combat';
  if (state === 'travel') return 'travel';
  if (state !== 'task') return 'idle';
  switch (family) {
    case 'mine':
    case 'chop':
      return 'mine_chop';
    case 'fish':
      return 'fish';
    case 'gather':
    case 'hunt':
      return 'gather';
    default:
      return 'craft';
  }
}

export function refreshFromVsj(): void {
  const fight = vsj.at('fight');
  let ca = vsj.at('current_activity');
  if (ca == null || typeof ca !== 'object') ca = {};
  const state = String(ca.state ?? 'idle');
  const fam = familyFor(String(ca.name ?? ''), String(ca.icon ?? ''));
  setSituation(situationFor(fight != null && typeof fight === 'object', state, fam));
  const tr = ca.travel;
  setRunning(state === 'travel' && tr != null && typeof tr === 'object' && !!tr.running);
}

/** Playback follows the vsj screen: boot/login stay silent; streams survive
    a stop, so re-entry is instant. */
function refreshScreenGate(): void {
  const allowed = MUSIC_SCREENS.includes(String(vsj.at('screen') ?? ''));
  if (allowed === screenOn) return;
  screenOn = allowed;
  if (allowed) {
    if (streams && !started) startPlayback();
  } else stopPlayers();
}

function stopPlayers(): void {
  for (const s of Object.keys(players) as Stem[]) {
    try { players[s]!.src.stop(); } catch { /* already stopped */ }
    players[s]!.src.disconnect();
    players[s]!.gain.disconnect();
  }
  players = {};
  started = false;
}

export function setSituation(s: string): void {
  if (s === situation) return;
  situation = s;
  applyMix();
}

/** Crossfade every stem to the situation's gain (instant at first start). */
function applyMix(now = false): void {
  const ctx = getCtx();
  if (!ctx || !started) return;
  const mix = SITUATION_MIX[situation] ?? SITUATION_MIX['idle'];
  const t = ctx.currentTime;
  for (const s of STEMS) {
    const p = players[s];
    if (!p) continue;
    const g = mix[s] ?? 0.0;
    p.gain.gain.cancelScheduledValues(t);
    if (now) p.gain.gain.setValueAtTime(g, t);
    else {
      p.gain.gain.setValueAtTime(p.gain.gain.value, t);
      p.gain.gain.linearRampToValueAtTime(g, t + FADE_S);
    }
  }
}

/** Travel-run lifts pitch/tempo a touch; identical ramp on every source
    keeps the stems phase-locked. */
export function setRunning(r: boolean): void {
  if (r === running) return;
  running = r;
  const ctx = getCtx();
  if (!ctx || !started) return;
  const t = ctx.currentTime;
  const target = r ? RUN_PITCH : 1.0;
  for (const s of STEMS) {
    const p = players[s];
    if (!p) continue;
    p.src.playbackRate.cancelScheduledValues(t);
    p.src.playbackRate.setValueAtTime(p.src.playbackRate.value, t);
    p.src.playbackRate.linearRampToValueAtTime(target, t + PITCH_FADE_S);
  }
}

/** Map view open = the soundtrack steps forward: sweep the low-pass fully
    open. Everywhere else it rests gently dampened under the game's sounds. */
export function setMapOpen(open: boolean): void {
  if (open === mapOpen) return;
  mapOpen = open;
  const ctx = getCtx();
  if (!ctx || !lpf) return;
  const t = ctx.currentTime;
  lpf.frequency.cancelScheduledValues(t);
  lpf.frequency.setValueAtTime(lpf.frequency.value, t);
  lpf.frequency.linearRampToValueAtTime(open ? LPF_OPEN_HZ : LPF_WORLD_HZ, t + LPF_FADE_S);
}

// ---------------------------------------------------------------- settings

export function setVolume(v: number, save = true): void {
  volume = Math.max(0, Math.min(1, v));
  applyVolume();
  if (save) saveAudioSettings({ music_volume: volume });
}

/** Follows the one Mute in sound settings (Sfx persists the flag). */
export function setMuted(m: boolean): void {
  muted = m;
  applyVolume();
}

function applyVolume(): void {
  if (musicBus) musicBus.gain.value = muted ? 0 : volume;
}

/** Recompute the vibe-derived timing. Pure state — no render side effects. */
function applyVibe(n: string): void {
  vibeName = n;
  vibe = VIBES[n] ?? VIBES['folk'];
  tempo = vibe.tempo;
  beatS = 60.0 / tempo;
  barS = beatS * 4.0;
  loopS = barS * BARS;
  swingAmt = vibe.swing;
}

/** Switch vibe: persist, then either swap to session-cached stems instantly
    or re-render async. The OLD vibe keeps playing until the new stems are
    ready — the swap is the only gap (the new mix fades in from silence). */
export function setVibe(n: string, save = true): void {
  if (n === vibeName || !VIBES[n]) return;
  applyVibe(n);
  if (save) saveAudioSettings({ music_vibe: n });
  const cached = streamCache[n];
  if (cached) swapTo(cached);
  else void startRender();
}

// ---------------------------------------------------------- async render

async function yield0(): Promise<void> {
  return new Promise((r) => setTimeout(r, 0));
}

/** Kick off (or queue behind) a full-piece render. One "thread" at a time;
    if one is mid-flight its completion sees the stale generation and
    immediately starts the fresh render. */
async function startRender(): Promise<void> {
  renderGen++;
  if (rendering) return;
  rendering = true;
  const gen = renderGen;
  const vn = vibeName;
  const bufs = await renderStems();
  rendering = false;
  if (gen === renderGen) {
    streamCache[vn] = bufs; // a stale render may be half-garbled
    swapTo(bufs);
  } else if (streamCache[vibeName]) {
    swapTo(streamCache[vibeName]); // newer pick mid-render, already cached
  } else {
    void startRender(); // newer pick mid-render: redo for the current vibe
  }
}

/** Render the whole piece for the current vibe: a few bars of one stem per
    slice, yielding between chunks so the frame never stalls.
    Exported as the test hook — pure math. */
export async function renderStems(): Promise<Record<Stem, Float32Array>> {
  const bufs = {} as StemData;
  for (const s of STEMS) bufs[s] = new Float32Array(Math.round(loopS * STEM_RATE[s]));
  for (const s of STEMS) {
    for (let g = 0; g < BARS; g += 4) {
      renderStemBars(s, bufs[s], g, Math.min(g + 4, BARS));
      await yield0();
    }
  }
  for (const s of STEMS) {
    finishStem(bufs[s]);
    await yield0();
  }
  return bufs;
}

function renderStemBars(s: Stem, buf: Float32Array, from: number, to: number): void {
  switch (s) {
    case 'pad':
      for (let bar = from; bar < to; bar++) renderPadBar(buf, bar);
      break;
    case 'melody':
      for (let bar = from; bar < to; bar++) renderMelodyBar(buf, bar);
      break;
    case 'perc':
      for (let bar = from; bar < to; bar++) renderPercBar(buf, bar);
      break;
    case 'bass':
      renderBassBars(buf, from, to);
      break;
    case 'sub':
      renderSubBars(buf, from, to);
      break;
    case 'war':
      renderWarBars(buf, from, to);
      break;
    case 'arp':
      renderArpBars(buf, from, to);
      break;
  }
}

function swapTo(data: StemData): void {
  stopPlayers();
  streams = data;
  startPlayback();
}

function startPlayback(): void {
  const ctx = getCtx();
  if (started || !screenOn || !streams || !ctx || ctx.state !== 'running' || !musicBus) return;
  started = true;
  const t0 = ctx.currentTime + 0.05;
  for (const s of STEMS) {
    const data = streams[s];
    const rate = STEM_RATE[s];
    const buf = ctx.createBuffer(1, data.length, rate);
    buf.copyToChannel(data as Float32Array<ArrayBuffer>, 0);
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.loop = true;
    src.loopStart = 0;
    src.loopEnd = buf.duration; // sample-exact seam
    src.playbackRate.value = running ? RUN_PITCH : 1.0;
    const g = ctx.createGain();
    g.gain.value = 0.0;
    src.connect(g);
    g.connect(musicBus);
    src.start(t0); // all stems start on the same clock tick, in sync
    players[s] = { src, gain: g };
  }
  applyMix(); // fade the current situation in from silence
}

// ------------------------------------------------------------- stem synth

/** Semitones from A4 -> Hz. */
function hz(st: number): number {
  return 440.0 * Math.pow(2.0, st / 12.0);
}

/** Swing lives here and ONLY here: "and" eighths (x.5 in the tables) land
    at x.swing at render time; everything else stays put. */
function swing(beat: number): number {
  const frac = beat - Math.floor(beat);
  if (Math.abs(frac - 0.5) < 0.01) return Math.floor(beat) + swingAmt;
  return beat;
}

/** Pad: three detuned near-sine voices per chord, tail lapping into the next
    bar (wrap-mix keeps the loop seam silent-gap-free). EDM pumps it. */
function renderPadBar(buf: Float32Array, bar: number): void {
  const ch = CHORDS[PROG[bar]];
  const pv = vibe.pad;
  for (const st of ch.pad)
    mtone(buf, RATE_LOW, bar * barS, barS + 0.8, hz(st), [[1.0, 1.0], [2.0, 0.12]],
      { amp: pv.amp, attack: pv.attack, curve: 1.4, detune: 0.004,
        trem_hz: pv.pump > 0.0 ? 1.0 / beatS : 0.0, trem_amt: pv.pump });
}

/** Bass: the vibe's groove rows [beat, use-fifth-below, dur-beats, amp].
    Rests on break bars. */
function renderBassBars(buf: Float32Array, from: number, to: number): void {
  for (let bar = from; bar < to; bar++) {
    if (BREAK_BARS.includes(bar)) continue;
    const ch = CHORDS[PROG[bar]];
    for (const h of vibe.bass.groove) {
      const st = ch.bass - (h[1] ? 5.0 : 0.0);
      mtone(buf, RATE_LOW, bar * barS + h[0] * beatS, h[2] * beatS + 0.1, hz(st), DARK,
        { amp: h[3], attack: 0.005, curve: 2.2, decay_scale: 0.9 });
    }
  }
}

/** Sub (combat-only stem): 808 sub-bass an octave under the bass — even bars
    punch trap-style syncopated hits, odd bars hold ONE note with a
    tempo-synced eighth-note amp wobble (the dubstep lever). */
function renderSubBars(buf: Float32Array, from: number, to: number): void {
  for (let bar = from; bar < to; bar++) {
    if (BREAK_BARS.includes(bar)) continue;
    const st = CHORDS[PROG[bar]].bass - 12.0;
    const t0 = bar * barS;
    if (bar % 2 === 0) {
      for (const h of [[0.0, 1.5, 1.0], [2.5, 0.5, 0.7], [3.5, 0.5, 0.85]])
        mtone(buf, RATE_LOW, t0 + h[0] * beatS, h[1] * beatS, hz(st),
          [[1.0, 1.0], [2.0, 0.15]], { amp: h[2], attack: 0.008, curve: 1.2 });
    } else {
      mtone(buf, RATE_LOW, t0, 3.5 * beatS, hz(st), [[1.0, 1.0], [2.0, 0.15]],
        { amp: 0.9, attack: 0.01, curve: 0.8, trem_hz: 2.0 / beatS, trem_amt: 0.85 });
    }
  }
}

/** Arp: the constant-subdivision layer. "picked" = broken-chord eighths
    (swung, accented); "skank" = short triad stabs on the offbeats (reggae).
    Plays THROUGH break bars (it's the breath). */
function renderArpBars(buf: Float32Array, from: number, to: number): void {
  const av = vibe.arp;
  for (let bar = from; bar < to; bar++) {
    const tones = CHORDS[PROG[bar]].arp;
    if (av.mode === 'skank') {
      for (const e of [0.5, 1.5, 2.5, 3.5])
        for (let ti = 1; ti < 4; ti++)
          mtone(buf, RATE_HIGH, bar * barS + swing(e) * beatS, 0.22,
            hz(tones[ti]), BAR,
            { amp: 0.22 * av.amp, attack: 0.002, curve: 2.4, decay_scale: av.decay });
      continue;
    }
    for (let i = 0; i < ARP_PATTERN.length; i++) {
      const st = tones[ARP_PATTERN[i]];
      mtone(buf, RATE_HIGH, bar * barS + swing(i * 0.5) * beatS, 0.4, hz(st), BAR,
        { amp: (i % 4 === 0 ? 0.34 : 0.24) * av.amp,
          attack: 0.003, curve: 2.0, decay_scale: av.decay });
    }
  }
}

/** Melody: the vibe's lead voice — sustained for the written note length
    (gated by the vibe's dur), vibrato blooming in after the onset. */
function renderMelodyBar(buf: Float32Array, bar: number): void {
  const lv = vibe.lead;
  for (const ev of MELODY_BARS[bar]) {
    const durS = ev[2] * beatS * lv.dur;
    const onBeat = ev[0] - Math.floor(ev[0]) < 0.01;
    mtone(buf, RATE_HIGH, bar * barS + swing(ev[0]) * beatS, durS, hz(ev[1]), lv.partials,
      { amp: (onBeat ? 0.52 : 0.42) * lv.amp, attack: lv.attack, curve: lv.curve,
        detune: 0.003, vib_hz: lv.vib_hz, vib_semis: lv.vib_semis, vib_delay: 0.15 });
  }
}

/** Perc: the vibe's kit. All kits rest on break bars and close each 8-bar
    half with a fill so long loops don't fatigue. */
function renderPercBar(buf: Float32Array, bar: number): void {
  if (BREAK_BARS.includes(bar)) return;
  const t0 = bar * barS;
  const fill = bar % 8 === 7;
  switch (vibe.kit) {
    case 'rnb': // lazy backbeat: kick 1 + and-of-2, snare 2/4, swung hats
      kick(buf, t0, 0.95);
      kick(buf, t0 + beatS * 2.5, 0.7);
      snare(buf, t0 + beatS, 0.5);
      if (fill) for (let i = 0; i < 4; i++) snare(buf, t0 + beatS * (3.0 + 0.25 * i), 0.25 + 0.07 * i);
      else snare(buf, t0 + beatS * 3.0, 0.55);
      for (let i = 0; i < 8; i++) hat(buf, t0 + beatS * swing(i * 0.5), i % 2 === 0 ? 0.12 : 0.17);
      break;
    case 'edm': // four-on-floor, clap 2/4, offbeat open hats
      for (let b = 0; b < 4; b++) kick(buf, t0 + beatS * b, b % 2 === 0 ? 0.95 : 0.85);
      snare(buf, t0 + beatS, 0.45);
      snare(buf, t0 + beatS * 3.0, 0.45);
      if (fill) for (let i = 0; i < 4; i++) snare(buf, t0 + beatS * (3.0 + 0.25 * i), 0.28 + 0.08 * i);
      for (const e of [0.5, 1.5, 2.5, 3.5]) hat(buf, t0 + beatS * e, 0.26);
      break;
    case 'reggae': // one drop: kick+snare TOGETHER on 3, offbeat ticks
      kick(buf, t0 + beatS * 2.0, 0.95);
      snare(buf, t0 + beatS * 2.0, 0.45);
      tamb(buf, t0 + beatS * swing(0.5), 0.2);
      tamb(buf, t0 + beatS * swing(1.5), 0.2);
      tamb(buf, t0 + beatS * swing(2.5), 0.2);
      tamb(buf, t0 + beatS * swing(3.5), fill ? 0.4 : 0.25);
      if (fill) snare(buf, t0 + beatS * 3.5, 0.35);
      break;
    case 'country': // train beat: boom-chick with straight running hats
      kick(buf, t0, 0.9);
      kick(buf, t0 + beatS * 2.0, 0.85);
      snare(buf, t0 + beatS, 0.5);
      snare(buf, t0 + beatS * 3.0, fill ? 0.4 : 0.55);
      hat(buf, t0 + beatS * 0.5, 0.2);
      hat(buf, t0 + beatS * 1.5, 0.2);
      hat(buf, t0 + beatS * 2.5, 0.2);
      hat(buf, t0 + beatS * 3.5, 0.2);
      snare(buf, t0 + beatS * 0.5, 0.14); // brush ghosts
      snare(buf, t0 + beatS * 2.5, 0.14);
      if (fill) for (let i = 0; i < 2; i++) snare(buf, t0 + beatS * (3.5 + 0.25 * i), 0.3 + 0.1 * i);
      break;
    default: // folk: timpani 1 (lighter 3), tambourine 2/4, swung ticks
      timp(buf, t0, 0.95);
      timp(buf, t0 + beatS * 2.0, 0.6);
      tamb(buf, t0 + beatS, 0.4);
      if (fill) for (let i = 0; i < 4; i++) tamb(buf, t0 + beatS * (3.0 + 0.25 * i), 0.25 + 0.07 * i);
      else tamb(buf, t0 + beatS * 3.0, 0.45);
      for (const e of [0.5, 1.5, 2.5, 3.5]) hat(buf, t0 + beatS * swing(e), 0.18);
  }
}

function timp(buf: Float32Array, at: number, amp: number): void {
  mtone(buf, RATE_HIGH, at, 0.3, 60.0, DARK,
    { amp, attack: 0.003, curve: 2.0, decay_scale: 0.9 });
}

/** Tambourine: two bright noise bursts 12 ms apart — the jingle doubles. */
function tamb(buf: Float32Array, at: number, amp: number): void {
  mnoise(buf, RATE_HIGH, at, 0.07, { amp, bp_hz: 4000.0, bp_q: 1.6, curve: 2.6 });
  mnoise(buf, RATE_HIGH, at + 0.012, 0.05, { amp: amp * 0.5, bp_hz: 4000.0, bp_q: 1.6, curve: 2.6 });
}

function hat(buf: Float32Array, at: number, amp: number): void {
  mnoise(buf, RATE_HIGH, at, 0.035, { amp, bp_hz: 3600.0, bp_q: 2.2, curve: 2.8 });
}

/** War (combat-only stem, same in every vibe): the driving straight kit —
    see music.gd. Kick/snare sit on quarter beats so they can't fight the
    swing; only the hats' "and"s are swung to lock with the arp. */
function renderWarBars(buf: Float32Array, from: number, to: number): void {
  for (let bar = from; bar < to; bar++) {
    if (BREAK_BARS.includes(bar)) continue;
    const t0 = bar * barS;
    kick(buf, t0, 1.0);
    kick(buf, t0 + beatS * 2.0, 0.85);
    if (bar % 2 === 1) kick(buf, t0 + beatS * 3.5, 0.55);
    snare(buf, t0 + beatS, 0.55);
    if (bar % 4 === 3) for (let i = 0; i < 4; i++) snare(buf, t0 + beatS * (3.0 + 0.25 * i), 0.32 + 0.08 * i);
    else snare(buf, t0 + beatS * 3.0, 0.6);
    for (let i = 0; i < 8; i++)
      hat(buf, t0 + beatS * swing(i * 0.5), i % 2 === 0 ? 0.16 : 0.22);
  }
}

function kick(buf: Float32Array, at: number, amp: number): void {
  mtone(buf, RATE_HIGH, at, 0.16, 60.0, DARK,
    { amp, attack: 0.003, curve: 2.4, decay_scale: 1.5 });
}

function snare(buf: Float32Array, at: number, amp: number): void {
  mnoise(buf, RATE_HIGH, at, 0.1, { amp, bp_hz: 1900.0, bp_q: 0.9, curve: 2.6 });
  mtone(buf, RATE_HIGH, at, 0.07, 185.0, DARK,
    { amp: amp * 0.5, attack: 0.002, curve: 2.6, decay_scale: 2.2 });
}

interface MToneOpts {
  amp?: number; attack?: number; curve?: number; detune?: number;
  decay_scale?: number; vib_hz?: number; vib_semis?: number; vib_delay?: number;
  trem_hz?: number; trem_amt?: number;
}

/** Additive partial stack mixed into a LOOP buffer with wrap-around indexing.
    Same two partial flavors as sfx tone() + the two-voice micro-detune;
    partials that would alias at the run-lift pitch are culled. */
function mtone(buf: Float32Array, rate: number, atS: number, dur: number,
  f0: number, partials: number[][], opts: MToneOpts = {}): void {
  if (!buf.length) return;
  const amp = opts.amp ?? 1.0;
  const attack = opts.attack ?? 0.01;
  const curve = opts.curve ?? 2.0;
  const detune = opts.detune ?? 0.002;
  const dscale = opts.decay_scale ?? 1.0;
  const vibHz = opts.vib_hz ?? 0.0;
  const vibSemis = opts.vib_semis ?? 0.0;
  const vibDelay = opts.vib_delay ?? 0.15;
  const tremHz = opts.trem_hz ?? 0.0;
  const tremAmt = opts.trem_amt ?? 0.0;
  const start = Math.floor(atS * rate);
  const n = Math.floor(dur * rate);
  const nyqSafe = (rate * 0.5 * 0.95) / PITCH_HEADROOM;
  const stack = partials.filter((p) => f0 * p[0] * (1.0 + detune) < nyqSafe);
  const a = Math.max(attack, 0.003);
  let phase = 0.0;
  const m = buf.length;
  for (let i = 0; i < n; i++) {
    const t = i / rate;
    let fmul = 1.0;
    if (vibHz > 0.0) {
      const bloom = Math.max(0.0, Math.min(1.0, (t - vibDelay) / 0.3));
      fmul = Math.pow(2.0, (vibSemis * bloom * Math.sin(TAU * vibHz * t)) / 12.0);
    }
    phase += (TAU * f0 * fmul) / rate;
    const rem = 1.0 - Math.max(t - a, 0.0) / Math.max(dur - a, 0.001);
    const eShared = (t < a ? t / a : 1.0) * Math.pow(Math.max(rem, 0.0), curve);
    const eModal = (t < a ? t / a : 1.0) * Math.max(0.0, Math.min(1.0, (dur - t) / 0.02));
    let v = 0.0;
    for (const p of stack) {
      let pg = p[1];
      if (p.length >= 3) pg *= eModal * Math.exp(-t * p[2] * dscale);
      else pg *= eShared;
      v += (Math.sin(phase * p[0] * (1.0 + detune)) +
        Math.sin(phase * p[0] * (1.0 - detune))) * 0.5 * pg;
    }
    if (tremHz > 0.0)
      v *= 1.0 - tremAmt * 0.5 * (1.0 + Math.sin(TAU * tremHz * t - Math.PI * 0.5));
    buf[(start + i) % m] += v * amp;
  }
}

/** Band-passed noise burst mixed into a LOOP buffer with wrap-around
    indexing. Seeded from its own start time so every render of the piece is
    identical (composition, not playback randomness). */
function mnoise(buf: Float32Array, rate: number, atS: number, dur: number,
  opts: { amp?: number; attack?: number; curve?: number; bp_hz?: number; bp_q?: number } = {}): void {
  if (!buf.length) return;
  const amp = opts.amp ?? 0.5;
  const attack = opts.attack ?? 0.002;
  const curve = opts.curve ?? 2.5;
  const bpHz = opts.bp_hz ?? 2000.0;
  const rng = mulberry32(Math.floor(atS * 1000.0) + 977);
  const f = 2.0 * Math.sin((Math.PI * Math.max(60.0, Math.min(bpHz, rate / 5.5))) / rate);
  const q1 = 1.0 / Math.max(opts.bp_q ?? 1.1, 0.4);
  let low = 0.0;
  let band = 0.0;
  const start = Math.floor(atS * rate);
  const n = Math.floor(dur * rate);
  const m = buf.length;
  const a = Math.max(attack, 0.002);
  for (let i = 0; i < n; i++) {
    const t = i / rate;
    low += f * band;
    const high = rng() * 2 - 1 - low - q1 * band;
    band += f * high;
    const e = (t < a ? t / a : 1.0) *
      Math.pow(Math.max(1.0 - Math.max(t - a, 0.0) / Math.max(dur - a, 0.001), 0.0), curve);
    buf[(start + i) % m] += band * 2.0 * e * amp;
  }
}

/** Normalize to the music bed target, cap the peak — in place (the whole
    stream loops forward at playback). */
function finishStem(buf: Float32Array): void {
  let peak = 0.0;
  let acc = 0.0;
  for (let i = 0; i < buf.length; i++) {
    peak = Math.max(peak, Math.abs(buf[i]));
    acc += buf[i] * buf[i];
  }
  const rms = Math.sqrt(acc / Math.max(1.0, buf.length));
  let gain = Math.pow(10, RMS_DB / 20) / Math.max(rms, 0.00001);
  gain = Math.min(gain, PEAK / Math.max(peak, 0.00001));
  const scale = 32000.0 / 32768.0; // match the original 16-bit packing level
  for (let i = 0; i < buf.length; i++)
    buf[i] = Math.max(-1.0, Math.min(1.0, buf[i] * gain)) * scale;
}
